import { SlaType } from "@oneleet/api_client";

export enum SlaField {
  General = "slaGeneralHours",
  PolicySigning = "slaPolicySigningHours",
  SecurityTraining = "slaSecurityTrainingHours",
  VendorAssessment = "slaVendorAssessmentHours",
  RiskAssessment = "slaRiskAssessmentHours",
  AccessReview = "slaAccessReviewHours",
  Informational = "slaInformationalHours",
  LowSeverity = "slaLowSeverityHours",
  MediumSeverity = "slaMediumSeverityHours",
  HighSeverity = "slaHighSeverityHours",
  Critical = "slaCriticalHours",
}

export const SEVEN_DAYS_IN_HOURS = 7 * 24;
export const FOURTEEN_DAYS_IN_HOURS = 14 * 24;
export const THIRTY_DAYS_IN_HOURS = 30 * 24;
export const NINETY_DAYS_IN_HOURS = 90 * 24;

export const SLA_TYPES: Record<
  SlaType,
  {
    name: string;
    description?: string;
    field: SlaField;
    defaultValueHours: number | null;
  }
> = {
  [SlaType.GENERAL]: {
    name: "General",
    description:
      "Timeframe to remediate alerting monitors if no other SLA applies",
    field: SlaField.General,
    defaultValueHours: SEVEN_DAYS_IN_HOURS,
  },
  [SlaType.POLICY_SIGNING]: {
    name: "Policy signing",
    description: "Timeframe for people to sign new or updated policies",
    field: SlaField.PolicySigning,
    defaultValueHours: FOURTEEN_DAYS_IN_HOURS,
  },
  [SlaType.SECURITY_TRAINING]: {
    name: "Security training",
    description:
      "Timeframe for people to complete new and annual security awareness trainings",
    field: SlaField.SecurityTraining,
    defaultValueHours: FOURTEEN_DAYS_IN_HOURS,
  },
  [SlaType.VENDOR_ASSESSMENT]: {
    name: "Vendor assessment",
    description: "Timeframe to complete security assessments for new vendors",
    field: SlaField.VendorAssessment,
    defaultValueHours: FOURTEEN_DAYS_IN_HOURS,
  },
  [SlaType.RISK_ASSESSMENT]: {
    name: "Risk assessment",
    description: "Timeframe to complete annual risk assessments",
    field: SlaField.RiskAssessment,
    defaultValueHours: THIRTY_DAYS_IN_HOURS,
  },
  [SlaType.ACCESS_REVIEW]: {
    name: "Access review",
    description: "Timeframe to complete annual access reviews",
    field: SlaField.AccessReview,
    defaultValueHours: THIRTY_DAYS_IN_HOURS,
  },
  [SlaType.INFORMATIONAL]: {
    name: "Informational",
    description:
      "Timeframe to remediate informational findings. Examples include security best practices, missing documentation, and outdated dependencies without known vulnerabilities",
    field: SlaField.Informational,
    defaultValueHours: null,
  },
  [SlaType.LOW_SEVERITY]: {
    name: "Low severity",
    description:
      "Timeframe to remediate low severity findings. Examples include missing headers, cookies without secure flags, and information disclosure with minimal impact",
    field: SlaField.LowSeverity,
    defaultValueHours: null,
  },
  [SlaType.MEDIUM_SEVERITY]: {
    name: "Medium severity",
    description:
      "Timeframe to remediate medium severity findings. Examples include cross-site scripting (XSS), Cross-Site Request Forgery (CSRF), and sensitive data exposure",
    field: SlaField.MediumSeverity,
    defaultValueHours: NINETY_DAYS_IN_HOURS,
  },
  [SlaType.HIGH_SEVERITY]: {
    name: "High severity",
    description:
      "Timeframe to remediate high severity findings. Examples include SQL injection, remote code execution, authentication bypasses, and broken access controls",
    field: SlaField.HighSeverity,
    defaultValueHours: THIRTY_DAYS_IN_HOURS,
  },
  [SlaType.CRITICAL]: {
    name: "Critical",
    description:
      "Timeframe to remediate critical findings. Examples include active exploitation, severe data breach, system compromise, and vulnerabilities affecting all users",
    field: SlaField.Critical,
    defaultValueHours: SEVEN_DAYS_IN_HOURS,
  },
};
